import { AxiosError, CanceledError, AxiosResponse } from 'axios';
import { JsonObject } from '../helpers';
import { BaseModel } from '../models/base.model';

export const SAFE_LIMIT = 1000;

export type ApiResponse = JsonObject | JsonObject[] | null;

export type ApiFileResponse = {
  name: string;
  summary: string;
  purpose: string;
};

export type ApiUploadResponse = {
  file?: ApiFileResponse;
  threadId: string;
  error?: string;
};

export type ServiceResponse = {
  error?: string;
};

export type ListResponse = {
  count: number;
  currentPage: number;
  data: JsonObject[];
  hasNext: boolean;
  hasPrevious: boolean;
  pageSize: number;
  totalPages: number;
};

export type FieldError = {
  type?: string;
  value: unknown;
  msg: string;
  path: string;
  location?: string;
};

type WriteResponse = {
  error?: string;
  fieldErrors?: FieldError[];
  data?: JsonObject;
};

export type CreateResponse<T = BaseModel> = WriteResponse & {
  created: boolean;
  model?: T;
};

export type PatchResponse = WriteResponse & {
  patched: boolean;
};

export type UpdateResponse = WriteResponse & {
  updated: boolean;
};

export type UpdateErrorHandler = (error: AxiosError) => UpdateResponse;

export const DEFAULT_SERVICE_ERROR = 'There was a service error';

export const handleResponse = (response: AxiosResponse | null): ApiResponse => {
  if (!response?.data) {
    return null;
  }

  if (Array.isArray(response?.data)) {
    return response?.data as JsonObject[];
  }

  return response?.data as JsonObject;
};

export const handleUpdateResponse = (response: AxiosResponse | null): UpdateResponse => {
  if (response?.status !== 204) {
    return { updated: false, error: DEFAULT_SERVICE_ERROR };
  }

  return { updated: true };
};

export const handleUpdateErrorResponse = (error: AxiosError): UpdateResponse => {
  if (!error.response) {
    return { updated: false, error: DEFAULT_SERVICE_ERROR };
  }

  const { status, data } = error.response;

  if (status === 400 && Array.isArray(data)) {
    return getSafeErrorResponse({ updated: false, fieldErrors: data as FieldError[] });
  }

  return { updated: false, error: DEFAULT_SERVICE_ERROR };
};

// https://github.com/axios/axios?tab=readme-ov-file#error-types
export const getAxiosErrorMessage = (
  err: unknown,
  errorCodes?: Record<number, string>
): { message: string; clientside: boolean } => {
  const { code = '', status = 0 } = (err || {}) as AxiosError;

  if (err instanceof CanceledError || code === 'ERR_CANCELED') {
    return { message: 'You cancelled the request', clientside: true };
  }

  let message = 'There was a service error';

  if (['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK', 'ERR_FR_TOO_MANY_REDIRECTS'].includes(code)) {
    message = 'There was a network error';
  }

  // user-defined response code
  if (errorCodes?.[status]) {
    message = errorCodes[status];
  }

  const clientside = [
    'ERR_BAD_OPTION_VALUE',
    'ERR_BAD_OPTION',
    'ERR_DEPRECATED',
    'ERR_CANCELED',
    'ERR_NOT_SUPPORT',
    'ERR_INVALID_URL',
  ].includes(code);

  return { message, clientside };
};

export const getSafeErrorResponse = <T extends CreateResponse | UpdateResponse | PatchResponse>(
  response: T
): T => {
  if (!response.fieldErrors?.length) {
    return response;
  }

  const safeFieldErrors: FieldError[] = [];

  response.fieldErrors.forEach((error) => {
    const { path, msg } = error;
    if (!path) {
      if (msg) {
        response.error = msg;
      }
      return;
    }
    safeFieldErrors.push(error);
  });

  response.fieldErrors = safeFieldErrors;

  return response;
};
